import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, STUDIO } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { storeImage } from "../lib/sync";
import { fileToDataUri } from "../lib/image";
import { relative } from "../lib/format";
import { printTable } from "../lib/printView";
import { AsBuiltModal } from "./AsBuilt";
import type { Snag } from "../lib/types";

const SEV_HUE: Record<string, string> = { high: "var(--danger)", medium: "var(--warn)", low: "var(--ink-3)" };
const STATUS_LABEL: Record<string, string> = { open: "Open", fixed: "Fixed — to verify", accepted: "Accepted" };

export default function Snags() {
  const p = useCurrent();
  const canEdit = useCan("stages");
  const session = useSession();
  const who = session?.name ?? STUDIO.principal;
  const [add, setAdd] = useState(false);
  const [check, setCheck] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "open" | "fixed" | "accepted">("all");
  if (!p) return null;
  const all = [...(p.snags ?? [])].sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  const list = filter === "all" ? all : all.filter((s) => s.status === filter);
  const open = all.filter((s) => s.status === "open").length;

  function exportCsv() {
    const rows = [["Ref", "Title", "Location", "Trade", "Severity", "Status", "Raised", "Note"], ...all.map((s) => [s.ref, s.title, s.location ?? "", s.trade ?? "", s.severity ?? "", s.status, s.raisedOn.slice(0, 10), s.note ?? ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `${(p!.code || p!.name).replace(/[^\w]+/g, "_")}_snags.csv`; a.click();
  }

  return (
    <Page>
      <PageHead kicker={`${p.name} · ${p.code}`} title="Snag list" />
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 18 }}>
        Defects and outstanding items at handover — photographed, located, tracked to a fix and signed off.
      </p>
      <div className="row wrap reveal" style={{ gap: 8, marginBottom: 20, alignItems: "center" }}>
        {(["all", "open", "fixed", "accepted"] as const).map((f) => (
          <button key={f} className={"chip" + (filter === f ? " on" : "")} style={{ fontSize: 12, textTransform: "capitalize", background: filter === f ? "var(--accent)" : "var(--card)", color: filter === f ? "#fff" : "var(--ink-2)", borderColor: filter === f ? "var(--accent)" : "var(--line-2)" }} onClick={() => setFilter(f)}>
            {f === "all" ? `All ${all.length}` : f === "open" ? `Open ${open}` : f}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {all.length > 0 && <button className="btn ghost sm" onClick={() => printTable({
          title: "Snag list", subtitle: `${p!.name} · ${p!.code}`, meta: [`${all.length} items`, `${open} outstanding`],
          columns: [{ key: "ref", label: "Ref", width: "60px" }, { key: "title", label: "Item" }, { key: "location", label: "Location" }, { key: "trade", label: "Trade", width: "80px" }, { key: "status", label: "Status", width: "70px" }],
          rows: list.map((s) => ({ ref: s.ref, title: s.title + (s.note ? ` — ${s.note}` : ""), location: s.location ?? "", trade: s.trade ?? "", status: STATUS_LABEL[s.status] })),
          note: "Signed off items are marked Accepted.",
        })}><Icon.report width={13} />Print</button>}
        {all.length > 0 && <button className="btn ghost sm" onClick={exportCsv}><Icon.download width={13} />CSV</button>}
        {canEdit && <button className="btn ghost sm" title="Compare a site photo against the drawing and flag differences" onClick={() => setCheck(true)}><Icon.spark width={13} />As-built check</button>}
        {canEdit && <button className="btn accent sm" onClick={() => setAdd(true)}><Icon.plus width={14} />Add snag</button>}
      </div>

      {all.length === 0 ? (
        <div className="card pad reveal" style={{ maxWidth: 560 }}>
          <div className="kicker">No snags recorded</div>
          <p className="muted" style={{ fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>Capture defects on site with a photo and a location — each one is numbered and tracked until it's fixed and accepted.</p>
          {canEdit && <button className="btn accent" style={{ marginTop: 14 }} onClick={() => setAdd(true)}><Icon.plus width={15} />Add the first snag</button>}
        </div>
      ) : (
        <div className="col" style={{ gap: 12 }}>
          {list.map((s) => (
            <div key={s.id} className="card pad reveal" style={{ borderLeft: `3px solid ${SEV_HUE[s.severity ?? "low"]}`, opacity: s.status === "accepted" ? 0.72 : 1 }}>
              <div className="row between wrap" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 10, minWidth: 0, alignItems: "baseline" }}>
                  <span className="badge accent mono" style={{ fontSize: 10.5 }}>{s.ref}</span>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{s.title}</span>
                </span>
                <span className="badge" style={{ fontSize: 9, color: s.status === "accepted" ? "var(--ok)" : undefined }}>{STATUS_LABEL[s.status]}</span>
              </div>
              {s.note && <div style={{ fontSize: 13.5, marginTop: 6, lineHeight: 1.55 }}>{s.note}</div>}
              <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{[s.location, s.trade, s.severity && `${s.severity} severity`, `by ${s.raisedBy}`, relative(s.raisedOn)].filter(Boolean).join(" · ")}</div>
              {s.photos.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 8, marginTop: 10 }}>
                  {s.photos.map((ph, i) => <button key={i} onClick={() => setZoom(ph)} style={{ padding: 0, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", cursor: "zoom-in", background: "none", lineHeight: 0 }}><img src={ph} alt="" style={{ width: "100%", height: 90, objectFit: "cover", display: "block" }} /></button>)}
                </div>
              )}
              {canEdit && (
                <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
                  {s.status === "open" && <button className="btn accent sm" onClick={() => actions.setSnagStatus(p.id, s.id, "fixed", who)}><Icon.check width={13} />Mark fixed</button>}
                  {s.status === "fixed" && <button className="btn accent sm" onClick={() => actions.setSnagStatus(p.id, s.id, "accepted", who)}><Icon.check width={13} />Accept</button>}
                  {s.status !== "open" && <button className="btn ghost sm" onClick={() => actions.setSnagStatus(p.id, s.id, "open", who)}>Reopen</button>}
                  <span style={{ flex: 1 }} />
                  <button className="iconbtn" style={{ width: 30, height: 30 }} title="Remove" onClick={() => { if (confirm(`Remove ${s.ref}?`)) actions.removeSnag(p.id, s.id); }}><Icon.close width={13} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {add && <AddSnagModal p={p} by={who} onClose={() => setAdd(false)} />}
      {check && <AsBuiltModal p={p} by={who} onClose={() => setCheck(false)} />}
      {zoom && <div onClick={() => setZoom(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.8)", display: "grid", placeItems: "center", zIndex: 100, padding: 24, cursor: "zoom-out" }}><img src={zoom} alt="" style={{ maxWidth: "92vw", maxHeight: "88vh", borderRadius: 10 }} /></div>}
    </Page>
  );
}

function AddSnagModal({ p, by, onClose }: { p: any; by: string; onClose: () => void }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [location, setLocation] = useState("");
  const [trade, setTrade] = useState("");
  const [severity, setSeverity] = useState<"high" | "medium" | "low">("medium");
  const [photos, setPhotos] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function addPhotos(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    for (const f of Array.from(files)) { try { const src = await storeImage(await fileToDataUri(f), p.client?.code); setPhotos((x) => [...x, src]); } catch { /* off */ } }
    setBusy(false);
  }
  return (
    <Modal open onClose={busy ? () => {} : onClose} title="Add a snag">
      <div className="col" style={{ gap: 14 }}>
        <div className="field"><label>What's wrong</label><input className="input" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Chipped tile at the entrance lobby" /></div>
        <div className="row" style={{ gap: 12 }}>
          <div className="field" style={{ flex: 1 }}><label>Location</label><input className="input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Level 1 · Lobby" /></div>
          <div className="field" style={{ flex: 1 }}><label>Trade</label><input className="input" value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="Tiling" /></div>
          <div className="field" style={{ width: 120 }}><label>Severity</label><select className="select" value={severity} onChange={(e) => setSeverity(e.target.value as any)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
        </div>
        <div className="field"><label>Note <span className="dim">(optional)</span></label><textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What needs doing." /></div>
        <label className="panel row between" style={{ padding: "11px 14px", borderStyle: "dashed", cursor: "pointer" }}>
          <span className="row" style={{ gap: 9 }}><Icon.image width={16} style={{ color: "var(--accent-ink)" }} /><span style={{ fontSize: 13.5 }}>{busy ? "Uploading…" : photos.length ? `${photos.length} photo${photos.length === 1 ? "" : "s"}` : "Add photos"}</span></span>
          <span className="btn soft sm">Browse</span>
          <input type="file" accept="image/*" multiple hidden disabled={busy} onChange={(e) => addPhotos(e.target.files)} />
        </label>
        {photos.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(80px,1fr))", gap: 6 }}>{photos.map((ph, i) => <img key={i} src={ph} alt="" style={{ width: "100%", height: 60, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} />)}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={busy || !title.trim()} onClick={() => { actions.addSnag(p.id, { title, note, location, trade, severity, photos, by }); onClose(); }}><Icon.check width={15} />Add snag</button>
        </div>
      </div>
    </Modal>
  );
}

export type { Snag };
